import * as THREE from 'three';
import type { UnitDefinition } from '../../../shared/game/world-types.js';
import { createShadowShroud, type ShadowShroud } from './shadow-shroud.js';
import { createSmokeHull, type SmokeUniforms } from './shadow-smoke.js';

/** The height the figure is modelled at, crown of the head to the tip of its smoke tail. */
export const SHADOW_MODEL_HEIGHT = 2.05;
/** How far the tail's tip hangs above the ground while it floats at rest. */
export const SHADOW_HOVER = .16;

/** The smoke's moving parts, driven by the animator every frame. */
export interface ShadowCloth { uTime: { value: number }; uStream: { value: THREE.Vector2 }; uFlutter: { value: number }; uFade: { value: number }; uRim: { value: number } }
export interface ShadowHand { wrist: THREE.Bone; fingers: THREE.Bone[][] }
/** An arm: the shoulder and elbow joints, plus a bend part-way down each bone (`bend`: upper, lower), so it curves instead of hinging like a stick. */
export interface ShadowArm { shoulder: THREE.Bone; bend: [THREE.Bone, THREE.Bone]; elbow: THREE.Bone; hand: ShadowHand }
export const SHADOW_FINGERS = ['index', 'middle', 'ring', 'pinky'] as const;
/**
 * The joints, by the names the clips use (`src/content/animation/clips.json`, body `shadow`): `hips` at the waist, `spine`, `chest`, `neck` and
 * `head` above it and `tail.1` to `tail.3` below; per side `upper_arm`, `upper_bend`, `forearm` (the elbow), `lower_bend`, `hand`,
 * `finger_<index|middle|ring|pinky>.1..3` and `thumb.1..2`. The `.R` arm is at +x, as on the human rig. Every joint rests unturned.
 */
export interface ShadowRig {
  root: THREE.Group; height: number;
  /** What the code moves under the clips: rising out of the pool, sinking and spreading as it dissolves, the jolt of a blow. Pivots at the ground. */
  body: THREE.Group;
  /** The root joint, at the waist: the clips float, lean and lunge the figure by it. */
  hips: THREE.Bone;
  /** The head, at the top of the neck. */
  head: THREE.Bone;
  arms: [ShadowArm, ShadowArm];
  joints: Map<string, THREE.Bone>;
  skeleton: THREE.Skeleton;
  cloth: ShadowCloth;
  /** The pool of dark under it and the smoke shed off it; they stay on the ground while the body floats and sinks. */
  shroud: ShadowShroud;
  /** The smoke wrapped round the body. */
  smoke: SmokeUniforms;
  /** The eyes' glow, which the animator brightens and dims. */
  glow: THREE.SpriteMaterial[];
  /** The claws and the eyes, which the animator fades with the smoke. */
  fading: THREE.Material[];
}

/** Repeatable noise, so every Shadow is the same shape. */
function hash(n: number) { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s); }

/**
 * The body's smoke: near black, matte and faintly lit at the rim. Each vertex carries `aFree` (0 where the shape holds, at the chest and head,
 * to 1 at the end of a tail or tendril) and `aSeed`; the shader churns the free smoke, streams it back while the figure glides, and thins the
 * ends into nothing with hashed alpha, so the overlapping tendrils need no sorting.
 */
function smokeMaterial(color: number, cloth: ShadowCloth) {
  const material = new THREE.MeshStandardMaterial({ color, roughness: 1, metalness: 0, side: THREE.DoubleSide, alphaHash: true });
  material.name = 'shadow-smoke';
  material.onBeforeCompile = shader => {
    Object.assign(shader.uniforms, cloth);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
        uniform float uTime; uniform vec2 uStream; uniform float uFlutter;
        attribute float aFree; attribute float aSeed; varying float vFree; varying vec3 vSmoke;`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
        vFree = aFree;
        float free = aFree * aFree;
        float phase = aSeed * 6.2832 + position.y * 3.1;
        // Even the held smoke stirs a little; the free ends churn.
        float churn = .008 + free * .06;
        transformed.x += sin(uTime * 2.3 + phase) * churn * uFlutter;
        transformed.z += cos(uTime * 1.9 + phase * 1.3) * churn * uFlutter;
        transformed.y += sin(uTime * 3.1 + phase * .7) * .02 * free * uFlutter;
        transformed.xz += uStream * free * .5;
        transformed.y += length(uStream) * free * .12;
        vSmoke = position * 6. + vec3(0., -uTime * .8, 0.);`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform float uFade; uniform float uRim; uniform float uTime; varying float vFree; varying vec3 vSmoke;
        float smHash(vec3 p) { return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
        float smNoise(vec3 p) {
          vec3 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
          return mix(mix(mix(smHash(i), smHash(i + vec3(1, 0, 0)), f.x), mix(smHash(i + vec3(0, 1, 0)), smHash(i + vec3(1, 1, 0)), f.x), f.y),
            mix(mix(smHash(i + vec3(0, 0, 1)), smHash(i + vec3(1, 0, 1)), f.x), mix(smHash(i + vec3(0, 1, 1)), smHash(i + vec3(1, 1, 1)), f.x), f.y), f.z);
        }`)
      .replace('#include <alphamap_fragment>', `#include <alphamap_fragment>
        // Rising streaks of thinner smoke break up the surface, more and more towards the free ends.
        float streak = smNoise(vSmoke) * .65 + smNoise(vSmoke * 2.3) * .35;
        // Its outline frays: surfaces seen edge-on thin away into the smoke wrapped round it, so no hard silhouette shows.
        float facing = abs(dot(normalize(vNormal), normalize(vViewPosition)));
        diffuseColor.a *= uFade * (1.0 - .75 * smoothstep(.6, 1.0, vFree)) * (1.0 - smoothstep(.55, .95, streak) * (.25 + .6 * vFree)) * smoothstep(.04, .55, facing);`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
        float rim = pow(1.0 - abs(dot(normal, normalize(vViewPosition))), 2.5);
        totalEmissiveRadiance += vec3(.05, .052, .07) * rim * uRim * (1.0 - .7 * vFree);`);
  };
  material.customProgramCacheKey = () => 'shadow-smoke';
  return material;
}

/** The claws: dark, horn-hard, with the smoke's faint cold rim. Its own material, so it fades with its Shadow alone. */
function clawMaterial(color: number) {
  const material = new THREE.MeshStandardMaterial({ color, roughness: .7, alphaHash: true }); material.name = 'shadow-claw';
  material.onBeforeCompile = shader => {
    shader.fragmentShader = shader.fragmentShader.replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
      totalEmissiveRadiance += vec3(.05, .055, .075) * pow(1.0 - abs(dot(normal, normalize(vViewPosition))), 3.0);`);
  };
  material.customProgramCacheKey = () => 'shadow-claw';
  return material;
}

/** A soft white point of light, for the glow round each eye. */
function glowTexture() {
  const size = 32, data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const r = Math.hypot(x + .5 - size / 2, y + .5 - size / 2) / (size / 2), a = Math.max(0, 1 - r) ** 2.2;
    data.set([255, 255, 255, Math.round(a * 255)], (y * size + x) * 4);
  }
  const texture = new THREE.DataTexture(data, size, size); texture.needsUpdate = true;
  return texture;
}

/**
 * The trunk as one lathed shape, its cross-section a little flat: a starved chest and narrow shoulders over a waist you could close a hand round,
 * thinning below the hips into a long tail of smoke instead of legs. `aFree` climbs down the tail so it churns and frays.
 */
function trunkGeometry() {
  // Radius at each height, crown of the neck down to the tail's tip.
  const outline: [number, number][] = [[1.7, .045], [1.66, .06], [1.6, .13], [1.54, .15], [1.44, .135], [1.3, .1], [1.18, .085], [1.06, .1], [.94, .105], [.78, .09], [.6, .07], [.42, .05], [.26, .03], [SHADOW_HOVER, .006]];
  // Rings every few centimetres, so the spine and tail bend it smoothly.
  const profile = outline.flatMap(([y, radius], i) => i === outline.length - 1 ? [[y, radius]] : [0, 1, 2].map(k => [THREE.MathUtils.lerp(y, outline[i + 1][0], k / 3), THREE.MathUtils.lerp(radius, outline[i + 1][1], k / 3)]));
  const columns = 18, positions: number[] = [], free: number[] = [], seeds: number[] = [], indices: number[] = [];
  profile.forEach(([y, radius], r) => {
    const loose = THREE.MathUtils.smoothstep(1.06 - y, 0, .9);
    for (let c = 0; c <= columns; c++) {
      const a = c / columns * Math.PI * 2, lump = 1 + .12 * Math.sin(a * 3 + y * 11) * loose + .05 * Math.sin(a * 5 - y * 7);
      positions.push(Math.sin(a) * radius * lump, y, Math.cos(a) * radius * lump * .78);
      free.push(loose); seeds.push(hash(c * 1.3 + r * .7));
      if (r < profile.length - 1 && c < columns) { const i = r * (columns + 1) + c; indices.push(i, i + columns + 1, i + 1, i + 1, i + columns + 1, i + columns + 2); }
    }
  });
  return smokeGeometry(positions, free, seeds, indices);
}

/** Torn tendrils of smoke hanging in a loose ring from the waist and trailing below the tail, each its own length, narrowing to a frayed point. */
function tendrilGeometry() {
  const columns = 14, rows = 16, top = 1.22, positions: number[] = [], free: number[] = [], seeds: number[] = [], indices: number[] = [];
  const radius = (y: number) => THREE.MathUtils.lerp(.04, .13, THREE.MathUtils.smoothstep(y, .2, 1.1)) * (y > 1.05 ? THREE.MathUtils.lerp(1, .75, (y - 1.05) / .17) : 1);
  for (let c = 0; c < columns; c++) {
    const seed = hash(c + 40), bottom = SHADOW_HOVER - .1 + seed * .4, width = Math.PI * 2 / columns * 1.1, start = c / columns * Math.PI * 2 - width / 2;
    const base = positions.length / 3;
    for (let r = 0; r <= rows; r++) {
      const t = r / rows, y = THREE.MathUtils.lerp(top, Math.max(.02, bottom), t), taper = 1 - .9 * THREE.MathUtils.smoothstep(t, .5, 1);
      for (let k = 0; k <= 2; k++) {
        const a = start + width * (.5 + (k / 2 - .5) * taper), r0 = radius(y) + .015 * Math.sin(a * 7 + t * 9 + seed * 5);
        positions.push(Math.sin(a) * r0, y, Math.cos(a) * r0 * .8);
        free.push(Math.pow(t, 1.1) * (.9 + seed * .2)); seeds.push(seed);
        if (r < rows && k < 2) { const i = base + r * 3 + k; indices.push(i, i + 3, i + 1, i + 1, i + 3, i + 4); }
      }
    }
  }
  return smokeGeometry(positions, free, seeds, indices);
}

/** A long, thin limb of smoke from `0` down to `-length`, tapering from `from` to `to`. */
function limbGeometry(length: number, from: number, to: number, freeFrom: number, freeTo: number) {
  const geometry = new THREE.CylinderGeometry(to, from, length, 9, 6, true); geometry.translate(0, -length / 2, 0);
  const position = geometry.getAttribute('position') as THREE.BufferAttribute, free: number[] = [], seeds: number[] = [];
  for (let i = 0; i < position.count; i++) {
    const along = -position.getY(i) / length, angle = Math.atan2(position.getX(i), position.getZ(i));
    // Knotted, uneven, more wisp than flesh.
    const swell = 1 + .18 * Math.sin(along * 9 + angle * 2) * Math.sin(angle * 3);
    position.setX(i, position.getX(i) * swell); position.setZ(i, position.getZ(i) * swell);
    free.push(THREE.MathUtils.lerp(freeFrom, freeTo, along)); seeds.push(hash(angle * 3 + along));
  }
  geometry.setAttribute('aFree', new THREE.Float32BufferAttribute(free, 1)); geometry.setAttribute('aSeed', new THREE.Float32BufferAttribute(seeds, 1));
  geometry.computeVertexNormals();
  return geometry;
}

/** The head: a long, narrow skull leaning out from the neck, its back trailing off into smoke. Origin at the top of the neck. */
function headGeometry() {
  const geometry = new THREE.SphereGeometry(.1, 18, 14);
  const position = geometry.getAttribute('position') as THREE.BufferAttribute, free: number[] = [], seeds: number[] = [];
  for (let i = 0; i < position.count; i++) {
    let x = position.getX(i), y = position.getY(i), z = position.getZ(i);
    x *= .78; y *= 1.3; z *= .95;
    // The crown and the back of the skull draw out and up into smoke; the jaw narrows to a point.
    const back = Math.max(0, -z / .1), crown = Math.max(0, y / .13);
    z -= back * back * .06; y += back * crown * .06;
    if (y < -.04) { const k = (-.04 - y) / .09; x *= 1 - k * .45; z += k * .02; }
    position.setXYZ(i, x, y + .1, z + .02); free.push(back * .5 + crown * back * .3); seeds.push(hash(i * .37));
  }
  geometry.setAttribute('aFree', new THREE.Float32BufferAttribute(free, 1)); geometry.setAttribute('aSeed', new THREE.Float32BufferAttribute(seeds, 1));
  geometry.computeVertexNormals();
  return geometry;
}

function smokeGeometry(positions: number[], free: number[], seeds: number[], indices: number[]) {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('aFree', new THREE.Float32BufferAttribute(free, 1)); geometry.setAttribute('aSeed', new THREE.Float32BufferAttribute(seeds, 1));
  geometry.setIndex(indices); geometry.computeVertexNormals();
  return geometry;
}

/** A clawed hand: a narrow palm, four long hooked fingers and a thumb, each joint a pivot the animator curls. Hangs from the wrist down. */
type Joint = (name: string, parent: THREE.Object3D, x: number, y: number, z?: number) => THREE.Bone;

/**
 * A clawed hand: a narrow palm, four long hooked fingers and a thumb, each joint a bone the clips curl. Hangs from the wrist down. The thumb
 * stands out from the palm and forward; its bones lie along that line, unturned, like every other joint.
 */
function hand(wrist: THREE.Bone, smoke: THREE.Material, claw: THREE.Material, side: 1 | -1, suffix: string, joint: Joint): ShadowHand {
  const palm = new THREE.Mesh(new THREE.BoxGeometry(.05, .08, .02), smoke);
  palm.position.y = -.04; wrist.add(palm);
  const fingers: THREE.Bone[][] = [], down = new THREE.Vector3(0, -1, 0);
  const bone = (length: number, radius: number, tip: boolean) => {
    const geometry = new THREE.CylinderGeometry(tip ? radius * .1 : radius * .8, radius, length, 5); geometry.translate(0, -length / 2, 0); return geometry;
  };
  for (let f = 0; f < 5; f++) {
    const thumb = f === 4, joints: THREE.Bone[] = [], name = thumb ? 'thumb' : `finger_${SHADOW_FINGERS[f]}`;
    const along = thumb ? down.clone().applyEuler(new THREE.Euler(-.4, 0, side * .7)) : down, lie = new THREE.Quaternion().setFromUnitVectors(down, along);
    const lengths = thumb ? [.05, .05] : [.075, .06, .07].map(length => length * (f === 0 || f === 3 ? .88 : 1));
    let parent: THREE.Object3D = wrist;
    lengths.forEach((length, j) => {
      const at = j === 0 ? thumb ? new THREE.Vector3(side * .028, -.03, .01) : new THREE.Vector3((f - 1.5) * .013, -.08, 0) : along.clone().multiplyScalar(lengths[j - 1]);
      const here = joint(`${name}.${j + 1}.${suffix}`, parent, at.x, at.y, at.z);
      // The last joint is the claw: a hooked, tapering point.
      const segment = new THREE.Mesh(bone(length, .0065, j === lengths.length - 1), claw); segment.quaternion.copy(lie); here.add(segment);
      joints.push(here); parent = here;
    });
    fingers.push(joints);
  }
  return { wrist, fingers };
}

/** A repeatable stream of numbers in 0..1, so every Shadow's smoke sits the same way. */
function stream(seed: number) { let state = seed; return () => (state = hash(state * 1.618 + .37)); }

/** Puffs over the trunk and the tail: thickest on the chest, smaller and looser down the tail. Samples for `createSmokeHull`. */
function trunkPuffs() {
  const random = stream(.11), samples: number[] = [];
  for (let i = 0; i < 70; i++) {
    const y = THREE.MathUtils.lerp(SHADOW_HOVER + .05, 1.66, Math.sqrt(random())), a = random() * Math.PI * 2;
    const width = y > 1.5 ? .13 : y > 1.2 ? .1 : y > .9 ? .1 : THREE.MathUtils.lerp(.02, .09, (y - SHADOW_HOVER) / .75);
    const loose = THREE.MathUtils.smoothstep(1.06 - y, 0, .9), r = width * (.55 + .5 * random());
    samples.push(Math.sin(a) * r, y, Math.cos(a) * r * .8, (width * 2.4 + .06) * (.8 + .4 * random()), random(), random() * 6.28, loose);
  }
  return samples;
}
/** Puffs round the head, leaving the eyes clear in front. */
function headPuffs() {
  const random = stream(.53), samples: number[] = [];
  for (let i = 0; i < 16; i++) {
    const a = random() * Math.PI * 2, y = .02 + random() * .2, front = Math.cos(a) > .55 && y > .08 && y < .18;
    const r = (front ? .03 : .07) + random() * .03;
    samples.push(Math.sin(a) * r, y, Math.cos(a) * r * (front ? .4 : 1) - .01, .13 + random() * .06, random(), random() * 6.28, .1 + random() * .2);
  }
  return samples;
}
/** Puffs along a limb segment hanging from `0` to `-length`, `width` across, looser toward its end. */
function limbPuffs(length: number, width: number, count: number, seed: number, looseFrom: number, looseTo: number) {
  const random = stream(seed), samples: number[] = [];
  for (let i = 0; i < count; i++) {
    const along = (i + random()) / count, a = random() * Math.PI * 2, r = width * .6 * random();
    samples.push(Math.sin(a) * r, -along * length, Math.cos(a) * r, width * 2.6 + .03 + random() * .03, random(), random() * 6.28, THREE.MathUtils.lerp(looseFrom, looseTo, along));
  }
  return samples;
}


/** Down the spine and the tail, each joint and the height (model units) it holds most of the trunk at; between two, the trunk is shared. */
const CHAIN: readonly [string, number][] = [['neck', 1.66], ['chest', 1.5], ['spine', 1.32], ['hips', 1.1], ['tail.1', .86], ['tail.2', .57], ['tail.3', .3]];

/** Skins smoke hanging off the trunk by height: each vertex to the two joints of `CHAIN` either side of it, smoothly shared. */
function skinByHeight(geometry: THREE.BufferGeometry, index: (name: string) => number) {
  const position = geometry.getAttribute('position'), indices: number[] = [], weights: number[] = [];
  for (let i = 0; i < position.count; i++) {
    const y = position.getY(i), below = CHAIN.findIndex(([, height]) => height <= y);
    const lower = below < 0 ? CHAIN.length - 1 : below, upper = below <= 0 ? lower : below - 1;
    const share = upper === lower ? 0 : THREE.MathUtils.smoothstep((CHAIN[upper][1] - y) / (CHAIN[upper][1] - CHAIN[lower][1]), 0, 1);
    indices.push(index(CHAIN[upper][0]), index(CHAIN[lower][0]), 0, 0); weights.push(1 - share, share, 0, 0);
  }
  geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(indices, 4)); geometry.setAttribute('skinWeight', new THREE.Float32BufferAttribute(weights, 4));
}

/**
 * A Shadow (Summon Shadow): a thin, shapeless thing of black smoke, a starved chest and long arms ending in claws, trailing into a tail of smoke
 * instead of legs, with two bright white eyes in a narrow head. Built in code on a skeleton of its own; its motion is clips made in Blender and
 * the animator layers the smoke, the eyes, rising and dissolving over them (`shadow-animator.ts`).
 */
export function createShadowModel(definition: UnitDefinition): ShadowRig {
  const appearance = definition.appearance, height = appearance?.height ?? SHADOW_MODEL_HEIGHT;
  const cloth: ShadowCloth = { uTime: { value: 0 }, uStream: { value: new THREE.Vector2() }, uFlutter: { value: 1 }, uFade: { value: 1 }, uRim: { value: 1 } };
  const smoke = smokeMaterial(appearance?.cloth ?? 0x0b0a0d, cloth), thin = smokeMaterial(appearance?.leather ?? 0x141217, cloth);
  const claw = clawMaterial(appearance?.skin ?? 0x1b191d);
  const root = new THREE.Group(); root.name = 'shadow-figure'; root.scale.setScalar(height / SHADOW_MODEL_HEIGHT);
  const body = new THREE.Group(); body.name = 'shadow-body'; root.add(body);
  const joints = new Map<string, THREE.Bone>(), restHeight = new Map<string, number>();
  const joint: Joint = (name, parent, x, y, z = 0) => {
    const bone = new THREE.Bone(); bone.name = name; bone.position.set(x, y, z); parent.add(bone); joints.set(name, bone);
    restHeight.set(name, y + (parent instanceof THREE.Bone ? restHeight.get(parent.name)! : 0));
    return bone;
  };
  const hips = joint('hips', body, 0, 1.1);
  const chest = joint('chest', joint('spine', hips, 0, .12), 0, .2), head = joint('head', joint('neck', chest, 0, .18), 0, .08, .02);
  joint('tail.3', joint('tail.2', joint('tail.1', hips, 0, -.1), 0, -.3), 0, -.27);
  const hull: SmokeUniforms = { uTime: { value: 0 }, uFade: { value: 1 }, uDensity: { value: 1 }, uScale: { value: 1 }, uOrtho: { value: 0 } };
  // The puffs over the trunk ride the joint nearest them.
  const puffs = new Map<string, number[]>(), samples = trunkPuffs();
  for (let i = 0; i < samples.length; i += 7) {
    const name = CHAIN.reduce((best, item) => Math.abs(item[1] - samples[i + 1]) < Math.abs(best[1] - samples[i + 1]) ? item : best)[0], list = puffs.get(name) ?? [];
    list.push(samples[i], samples[i + 1] - restHeight.get(name)!, ...samples.slice(i + 2, i + 7)); puffs.set(name, list);
  }
  for (const [name, list] of puffs) joints.get(name)!.add(createSmokeHull(list, hull));
  const skull = new THREE.Mesh(headGeometry(), smoke); skull.name = 'shadow-skull'; skull.castShadow = true; head.add(skull);
  head.add(createSmokeHull(headPuffs(), hull));
  // The eyes: two hard white points, and a soft glow round each that the animator breathes.
  const eye = new THREE.MeshBasicMaterial({ color: 0xffffff, alphaHash: true, toneMapped: false }); eye.name = 'shadow-eye';
  const halo = glowTexture(), glow: THREE.SpriteMaterial[] = [];
  for (const side of [1, -1]) {
    const point = new THREE.Mesh(new THREE.SphereGeometry(.017, 8, 6), eye); point.name = 'shadow-eye'; point.position.set(side * .034, .125, .095); point.scale.set(1.2, .8, .6); head.add(point);
    const light = new THREE.SpriteMaterial({ map: halo, color: 0xe8eeff, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false, fog: true });
    const sprite = new THREE.Sprite(light); sprite.name = 'shadow-eye-glow'; sprite.scale.setScalar(.17); sprite.position.copy(point.position).add(new THREE.Vector3(0, 0, .01)); sprite.raycast = () => {}; head.add(sprite);
    glow.push(light);
  }
  // Each bone of the arm is two segments with a joint between, so the arm can curve like a tentacle of smoke; each segment wears its own puffs.
  const segment = (bone: THREE.Bone, next: string, length: number, from: number, to: number, freeFrom: number, freeTo: number, seed: number) => {
    const mesh = new THREE.Mesh(limbGeometry(length + .02, from, to, freeFrom, freeTo), smoke); mesh.castShadow = true; bone.add(mesh);
    bone.add(createSmokeHull(limbPuffs(length, (from + to) / 2, 5, seed, freeFrom, freeTo), hull));
    return joint(next, bone, 0, -length);
  };
  const arms = ([1, -1] as const).map((side, i) => {
    const suffix = side === 1 ? 'R' : 'L', shoulder = joint(`upper_arm.${suffix}`, chest, side * .17, .14);
    const upperBend = segment(shoulder, `upper_bend.${suffix}`, .215, .042, .036, .05, .12, .2 + i * .1);
    const elbow = segment(upperBend, `forearm.${suffix}`, .215, .036, .03, .12, .2, .25 + i * .1);
    const lowerBend = segment(elbow, `lower_bend.${suffix}`, .205, .032, .027, .15, .25, .3 + i * .1);
    const wrist = segment(lowerBend, `hand.${suffix}`, .205, .027, .022, .25, .35, .35 + i * .1);
    return { shoulder, bend: [upperBend, lowerBend], elbow, hand: hand(wrist, smoke, claw, side, suffix, joint) };
  }) as [ShadowArm, ShadowArm];
  // The trunk and the tendrils bend with the spine and the tail.
  const skeleton = new THREE.Skeleton([...joints.values()]), index = (name: string) => skeleton.bones.indexOf(joints.get(name)!);
  root.updateMatrixWorld(true);
  for (const [name, geometry, material] of [['shadow-trunk', trunkGeometry(), smoke], ['shadow-tendrils', tendrilGeometry(), thin]] as const) {
    skinByHeight(geometry, index);
    const mesh = new THREE.SkinnedMesh(geometry, material); mesh.name = name; mesh.castShadow = name === 'shadow-trunk'; mesh.frustumCulled = false;
    body.add(mesh); mesh.updateMatrixWorld(true); mesh.bind(skeleton);
  }
  let seed = 7; for (const char of definition.id) seed = (seed * 31 + char.charCodeAt(0)) % 9973;
  const shroud = createShadowShroud(seed / 9973 + .001);
  // Smoke and shadow on the ground are no surface to hit or to point at.
  for (const part of [shroud.pool, shroud.wisps]) { part.raycast = () => {}; root.add(part); }
  return { root, height, body, hips, head, arms, joints, skeleton, cloth, shroud, smoke: hull, glow, fading: [claw, eye] };
}
